import { roundedToNumber, roundHeliosNumber } from 'utils/helios/rounding';
import type { RoundedToValue } from 'types/dataHelios';

const THOUSANDS_SEPARATOR_MAP = {
  Space: ' ',
  Dot: '.',
  Comma: ',',
} as const;

type ThousandsSeparator = keyof typeof THOUSANDS_SEPARATOR_MAP;

interface FormatHeliosNumberOptions {
  minimumFractionDigits: number;
  maximumFractionDigits: number;
  thousandsSeparator?: string | null;
  roundTo?: string | null;
  mathRound?: string | null;
}

const INTEGER_DATA_TYPES = new Set(['Int32', 'Int64']);
const NUMERIC_DATA_TYPES = new Set(['Int32', 'Int64', 'Decimal']);

/** Upper bound for fraction digits accepted by `Intl.NumberFormat` (ECMA-402). */
const MAX_FRACTION_DIGITS = 100;

const INCOMPLETE_NUMERIC = new Set(['', '-', '.', ',', '-.', '-,']);

/**
 * Converts localized decimal/thousands separators to a dot-decimal string.
 * - Only `,` → treat last comma as decimal (e.g. `15,0` → `15.0`)
 * - Both `.` and `,` → last separator is decimal, the other is grouping
 */
export const normalizeHeliosNumericSeparators = (value: string): string => {
  let normalized = value.replace(/\s/g, '');
  const hasComma = normalized.includes(',');
  const hasDot = normalized.includes('.');

  if (hasComma && hasDot) {
    const lastComma = normalized.lastIndexOf(',');
    const lastDot = normalized.lastIndexOf('.');
    if (lastComma > lastDot) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (hasComma) {
    const lastComma = normalized.lastIndexOf(',');
    const intPart = normalized.slice(0, lastComma).replace(/,/g, '');
    const fracPart = normalized.slice(lastComma + 1);
    normalized = `${intPart}.${fracPart}`;
  }

  return normalized;
};

/**
 * Number of decimal places of the shortest decimal representation of `numeric`.
 * Handles exponential notation, e.g. `0.0000001` (`"1e-7"`) → 7, `1e+21` → 0.
 */
export const getDecimalPlaces = (numeric: number): number => {
  const valueString = String(numeric);
  const exponentialMatch = valueString.match(/^([+-]?\d+)(?:\.(\d+))?[eE]([+-]?\d+)$/);
  if (exponentialMatch) {
    const fractionDigits = exponentialMatch[2]?.length ?? 0;
    const exponent = Number(exponentialMatch[3]);
    return Math.min(MAX_FRACTION_DIGITS, Math.max(0, fractionDigits - exponent));
  }
  const dotIndex = valueString.indexOf('.');
  return dotIndex >= 0 ? valueString.length - dotIndex - 1 : 0;
};

/**
 * Parses a Helios numeric form/API value for the given column data type.
 * Int32/Int64 are truncated toward zero; incomplete values return null.
 */
const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (INCOMPLETE_NUMERIC.has(trimmed)) {
    return null;
  }

  const normalized = normalizeHeliosNumericSeparators(trimmed);
  if (INCOMPLETE_NUMERIC.has(normalized) || normalized === '') {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Parses a Helios numeric value — a number or a locale-formatted string that may
 * contain thousands separators / a decimal comma ("1 000", "1.234,56", "15,0",
 * "-5") — into a finite number, or null when invalid. A lone `.` is treated as
 * the decimal separator (same convention as the Helios side).
 */
export const parseHeliosNumericValue = (value: unknown): number | null => toFiniteNumber(value);

export const parseHeliosNumericRawValue = (value: unknown, dataType: string): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numeric = toFiniteNumber(value);
  if (numeric === null) {
    return null;
  }

  if (INTEGER_DATA_TYPES.has(dataType)) {
    return Math.trunc(numeric);
  }

  return numeric;
};

/** Form write-back helper: integer/decimal string, or empty when invalid. */
export const toHeliosNumericFormValue = (value: unknown, dataType: string): string => {
  const parsed = parseHeliosNumericRawValue(value, dataType);
  return parsed === null ? '' : String(parsed);
};

export const isHeliosNumericDataType = (dataType: string): boolean => NUMERIC_DATA_TYPES.has(dataType);

export const formatNumberAsTime = (value: number): string => {
  if (!Number.isFinite(value)) {
    return '';
  }

  const absValue = Math.abs(value);
  let hours = Math.floor(absValue);
  let minutes = Math.round((absValue - hours) * 60);
  if (minutes === 60) {
    hours++;
    minutes = 0;
  }
  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

export const formatHeliosNumber = (value: number, locale: string, options: FormatHeliosNumberOptions): string => {
  if (!Number.isFinite(value)) {
    return '';
  }

  let { minimumFractionDigits, maximumFractionDigits } = options;
  let numericValue = value;

  if (options.roundTo) {
    const digits = roundedToNumber[options.roundTo as RoundedToValue];
    if (digits !== undefined) {
      numericValue = roundHeliosNumber(value, options.roundTo, options.mathRound);
      minimumFractionDigits = Math.max(0, digits);
      maximumFractionDigits = Math.max(0, digits);
    }
  }

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping: Boolean(options.thousandsSeparator),
  });

  const separator = options.thousandsSeparator ? THOUSANDS_SEPARATOR_MAP[options.thousandsSeparator as ThousandsSeparator] : undefined;
  if (!separator) {
    return formatter.format(numericValue);
  }

  return formatter
    .formatToParts(numericValue)
    .map((part) => (part.type === 'group' ? separator : part.value))
    .join('');
};
